import { useState } from "react";
import { meetingManager } from "../api/meetings";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

export const useCreateMeeting = ({ refetch, initialData, mode = 'create' }: { refetch: () => void, initialData?: any, mode?: 'create' | 'edit' }) => {

  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [showApplyAllDialog, setShowApplyAllDialog] = useState(false);

  const [formData, setFormData] = useState({
    meeting_label: initialData?.title || null,
    meeting_from_date: initialData?.start_date || new Date().toISOString().split('T')[0] + ' ' + new Date().toISOString().split('T')[1].split('.')[0],
    meeting_to_date: initialData?.end_date || null,
    start_time: initialData?.start_time || null,
    end_time: initialData?.end_time || null,
    meeting_location: initialData?.location || null,
    is_recurring: initialData?.is_recurring || false,
    recurring_type: initialData?.recurring_type || 'weekly',
    recurring_count: initialData?.recurring_count || 1,
    recurring_weekdays: initialData?.recurring_weekdays || [],
    monthly_recurring_option: initialData?.monthly_recurring_option || 'day_of_month',
    meeting_category: initialData?.category || null,
    meeting_template: initialData?.template_name || null,
    agenda: initialData?.agenda || (null as string | null),
    additional_points: initialData?.additional_points || null,
    attendees: initialData?.attendees_detail.filter((att: any) => att.user_id).map((att: any) => att.user_id) || [],
    allowExternal: initialData?.external_attendees_detail?.length > 0 || false,
    externalAttendees: initialData?.external_attendees_detail?.map((att: any) => ({
      name: att.full_name,
      email: att.email_id
    })) || [] as { name: string; email: string }[],
    meetingType: initialData?.projects?.length === 0 ? 'programme' : 'projects',
    projects: initialData?.projects?.length === 0 ? null : initialData?.sections.find((section: any) => section.update_key === 'projects')?.data?.map((project: any): AutocompleteOption => ({
      id: project.project_id,
      label: project.project_title,
      title: project.project_title,
      description: project.description,
      project_reference: project.project_ref,
      portfolioId: undefined,
      portfolioName: undefined
    })),
    programme: initialData?.programme ? {
      id: initialData?.programme_id,
      programme_name: initialData?.programme,
      description: initialData?.programme,
    } : null,
    isApplyAll: false,
  });

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (step === 0) {
      // Validate Schedule step
      if (!formData.meeting_label) {
        newErrors.meeting_label = "Meeting Label is required";
      }
      if (!formData.meeting_from_date) {
        newErrors.meeting_from_date = "Meeting From Date is required";
      }
      if (formData.is_recurring) {
        if (!formData.meeting_to_date) {
          newErrors.meeting_to_date = "Meeting To Date is required";
        }
      }
      if (!formData.start_time) {
        newErrors.start_time = "Start Time is required";
      }
      if (!formData.end_time) {
        newErrors.end_time = "End Time is required";
      }
      if (formData.meeting_from_date && formData.meeting_to_date) {
        const fromDate = new Date(formData.meeting_from_date);
        const toDate = new Date(formData.meeting_to_date);
        if (fromDate >= toDate) {
          newErrors.meeting_to_date = "To Date must be after From Date";
        }
      }
      if (!formData.meeting_location) {
        newErrors.meeting_location = "Meeting Location is required";
      }
      if (formData.is_recurring && !formData.recurring_type) {
        newErrors.recurring_type = "Recurring Type is required";
      }
      if (mode !== 'edit' && formData.is_recurring && formData.recurring_type === 'weekly' && (!formData.recurring_weekdays || formData.recurring_weekdays.length === 0)) {
        newErrors.recurring_weekdays = "Select a weekday for recurring meetings";
      }
    } else if (step === 1) {
      // Validate Agenda step
      if (!formData.meeting_category) {
        newErrors.meeting_category = "Meeting Category is required";
      }
      if (!formData.meeting_template) {
        newErrors.meeting_template = "Meeting Template is required";
      }
    } else if (step === 2) {
      // Validate Attendees step
      if (!formData.attendees || formData.attendees.length === 0) {
        newErrors.attendees = "At least one attendee is required";
      }
      if (formData.allowExternal && formData.externalAttendees && formData.externalAttendees.length > 0) {
        formData.externalAttendees.forEach((attendee: any, index: number) => {
          if (!attendee.name) {
            newErrors[`external_name_${index}`] = "Full Name is required";
          } else if (!/^[A-Za-z\s]+$/.test(attendee.name)) {
            newErrors[`external_name_${index}`] = "Full Name should only contain letters and spaces";
          }
          if (!attendee.email) {
            newErrors[`external_email_${index}`] = "Email is required";
          } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(attendee.email)) {
            newErrors[`external_email_${index}`] = "Invalid email format";
          }
        });
      }
    } else if (step === 3) {
      // Handle both single and array values
      if (formData.meetingType === 'projects') {
        if (!formData.projects || (Array.isArray(formData.projects) && formData.projects.length === 0)) {
          newErrors.projects = "At least one project is required";
        }
      } else if (formData.meetingType === 'programme') {
        if (!formData.programme || (Array.isArray(formData.programme) && formData.programme.length === 0)) {
          newErrors.programme = "At least one programme is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setErrors({});
  };

  const handleChange = (key: string, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [key]: value,
      };
      if (key === 'is_recurring' && value === true && updated.meeting_from_date) {
        const fromDate = new Date(updated.meeting_from_date);
        const endDate = new Date(fromDate);
        endDate.setMonth(endDate.getMonth() + 1);
        updated.meeting_to_date = endDate.toISOString().split('T')[0] + ' ' + fromDate.toISOString().split('T')[1].split('.')[0];
        updated.recurring_count = 1;
      }
      if (key === 'meeting_from_date' && updated.is_recurring && value) {
        const fromDate = new Date(value);
        const endDate = new Date(fromDate);
        endDate.setMonth(endDate.getMonth() + 1);
        updated.meeting_to_date = endDate.toISOString().split('T')[0] + ' ' + fromDate.toISOString().split('T')[1].split('.')[0];
      }
      return updated;
    });
    setErrors({});
  };

  const onChangeExternalAttendee = (id: number, key: string, value: string) => {
    setFormData(prev => {
      const updatedAttendees = prev.externalAttendees.map((attendee: any, index: number) => {
        if (index === id) {
          return {
            ...attendee,
            [key]: value,
          };
        }
        return attendee;
      });
      return {
        ...prev,
        externalAttendees: updatedAttendees,
      };
    });
    setErrors({});
  }

  const addExternalAttendee = () => {
    setFormData(prev => ({
      ...prev,
      externalAttendees: [...(prev.externalAttendees || []), { name: '', email: '' }]
    }));
  }

  const deleteExternalAttendee = (id: number) => {
    setFormData(prev => ({
      ...prev,
      externalAttendees: prev.externalAttendees.filter((_: any, index: number) => index !== id)
    }));
  }

  const onSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    // Check if this is a recurring meeting in edit mode
    if (mode === 'edit' && initialData && initialData.is_recurring) {
      setShowApplyAllDialog(true);
      return;
    }

    // Proceed with submission for non-recurring meetings
    await submitMeeting();
  };

  const handleApplyAllResponse = async (applyToAll: boolean) => {
    setShowApplyAllDialog(false);
    setFormData(prev => ({
      ...prev,
      isApplyAll: applyToAll
    }));
    await submitMeeting(applyToAll);
  };

  const submitMeeting = async (applyToAll?: boolean) => {
    try {
      setLoading(true);
      setSubmitError(null);
      if (mode === 'edit' && initialData) {
        // For edit mode, use update_key format
        const updates = [
          { update_key: 'title', new_value: formData.meeting_label },
          { update_key: 'location', new_value: formData.meeting_location },
          { update_key: 'category', new_value: formData.meeting_category },
          { update_key: 'additional_points', new_value: formData.additional_points || '' },
          { update_key: 'start_date', new_value: formData.meeting_from_date },
          { update_key: 'start_time', new_value: formData.start_time },
          { update_key: 'end_time', new_value: formData.end_time },
          { update_key: 'attendees', new_value: Array.isArray(formData.attendees) ? formData.attendees.map(att => typeof att === 'object' ? att : { user_id: att, attended: null }) : [] },
          { update_key: 'is_apply_all', new_value: applyToAll !== undefined ? String(applyToAll) : String(formData.isApplyAll) },
          { update_key: 'template_name', new_value: formData.meeting_template },
        ];
        if (formData.meeting_to_date) {
          updates.push({ update_key: 'end_date', new_value: formData.meeting_to_date })
        }
        if (formData.meetingType === 'projects') {
          updates.push({ update_key: 'projects', new_value: Array.isArray(formData.projects) ? formData.projects.map((proj: AutocompleteOption) => proj.project_reference) : (formData.projects ? [formData.projects.project_reference] : []) });
        } else if (formData.meetingType === 'programme' && formData.programme) {
          updates.push({ update_key: 'programme', new_value: formData.programme.programme_name });
          updates.push({ update_key: 'projects', new_value: [] });
        }
        if (formData.recurring_type === 'monthly') {
          updates.push({ update_key: 'monthly_pattern', new_value: formData.monthly_recurring_option });
        }
        if (formData.externalAttendees) {
          updates.push({ update_key: 'external_attendees', new_value: formData.externalAttendees.map((att: { name: any; email: any; }) => ({ full_name: att.name, email: att.email, attended: null })) });
        }
        await meetingManager.update(initialData.id, updates);
      } else {
        // For create mode, use standard object format
        const params = {
          title: formData.meeting_label,
          location: formData.meeting_location,
          category: formData.meeting_category,
          template_name: formData.meeting_template,
          additional_points: formData.additional_points || "",
          attendees: formData.attendees,
          external_attendees: Array.isArray(formData.externalAttendees) ? formData.externalAttendees.map((att: { name: any; email: any }) => ({ full_name: att.name, email: att.email, attended: null, })) : [],
          start_date: formData.meeting_from_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          end_date: formData.meeting_to_date,
          is_recurring: formData.is_recurring,
          recurring_type: formData.recurring_type || "",
          repeat_every: Number(formData.recurring_count) || 1,
          weekly_days: formData.recurring_weekdays || [],
          monthly_pattern: formData.recurring_type === 'monthly' ? formData.monthly_recurring_option : null,
          projects: formData.meetingType === 'projects' ? Array.isArray(formData.projects) ? formData.projects.map((proj: AutocompleteOption) => proj.project_reference) : [] : null,
          portfolios: formData.meetingType === 'portfolios' ? Array.isArray(formData.projects) ? formData.projects.map((proj: AutocompleteOption) => proj.project_reference) : [] : null,
          programme: (formData.meetingType === 'programme' && formData.programme) ? formData.programme.programme_name : null,
        };
        await meetingManager.create(params);
      }
      refetch();
      setActiveStep(0);
      setFormData({
        meeting_label: null,
        meeting_from_date: null,
        meeting_to_date: null,
        start_time: null,
        end_time: null,
        meeting_location: null,
        is_recurring: false,
        recurring_type: 'weekly',
        recurring_count: 1,
        recurring_weekdays: [],
        monthly_recurring_option: 'day_of_month',
        meeting_category: null,
        meeting_template: null,
        agenda: null,
        additional_points: null,
        attendees: [],
        allowExternal: false,
        externalAttendees: [],
        projects: null,
        programme: null,
        isApplyAll: false,
        meetingType: 'projects'
      });
    } catch (error: any) {
      setSubmitError(error.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} meeting`);
    } finally {
      setLoading(false);
    }
  };

  return {
    errors, loading, submitError, activeStep, formData,
    handleNext, handleBack, handleChange, addExternalAttendee,
    deleteExternalAttendee, onChangeExternalAttendee, onSubmit,
    showApplyAllDialog, handleApplyAllResponse
  };

};